/* =============================================================================
   VIEWS / ADMIN / FOOD-COST-REPORT — Wareneinsatz-Übersicht (S158 ERP Phase B)
   =============================================================================
   Subtab „Food-Cost" der Gruppe „Ware" (S157). Tabelle aller verkaufbaren Gerichte:
   Name · Verkaufspreis · Wareneinsatz · Food-Cost % · Marge € · Ampel — sortierbar
   (v. a. nach FC%/Marge, um Ausreißer zu finden). Wareneinsatz je Gericht über
   `composeCostSync(id, {})` (Default-Gericht inkl. Default-Optionen) aus dem
   nutrition-cache (lokal, kein Roundtrip; Realtime-fest). Ziel-% global oben
   sichtbar/änderbar (Schreibweg in Settings). Fehlende Einkaufsdaten = grau + Hinweis.

   Hinter Feature-Flag `ingredientsDb`. Inline-Handler ohne window.func() (CSP).
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { foodCostPct, marginEuro, ampelFor, targetPctFor, formatEuro, formatPct, AMPEL_COLORS } from '../../core/food-cost.js';
import { isRankOpen } from './stats-collapse.js';   // S217 — Top-5 + „alle anzeigen" (gemeinsame Mechanik)

const AMPEL_COLOR = AMPEL_COLORS; // S166 — zentrale Ampel-Farben (core/food-cost.js)
const AMPEL_LABEL = { green: 'im Ziel', yellow: 'grenzwertig', red: 'über Ziel', unknown: 'Einkaufsdaten fehlen' };

// Sortzustand (Default: schlechtester Food-Cost zuerst → Ausreißer oben).
let _sortKey = 'pct';
let _sortDir = 'desc';

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }
function menuState() {
  const st = storage() && storage()._getInternalState && storage()._getInternalState();
  return (st && st.menu) || [];
}
function globalTarget() {
  try {
    const st = storage() && storage()._getInternalState && storage()._getInternalState();
    const v = st && st.settings && st.settings.foodCostTargetPct;
    if (v != null && isFinite(Number(v)) && Number(v) > 0) return Number(v);
  } catch (_) {}
  return 30;
}

// S189 — der Report lebt jetzt in Auswertung → Statistik → Warenwirtschaft
// (integriert), nicht mehr als eigener „Ware"-Tab. Host ist konfigurierbar,
// damit fcSort/fcSetTarget denselben Container re-rendern.
let _hostId = 'tab-food-cost';
function _host() { return document.getElementById(_hostId); }

/** Rendert den Food-Cost-Report in einen beliebigen Host (Default: alter Tab). */
export async function renderFoodCostInto(hostId) {
  _hostId = hostId || 'tab-food-cost';
  const el = _host();
  if (!el) return;
  if (!flagOn()) {
    el.innerHTML = `<div class="card"><p class="hint">Food-Cost ist deaktiviert (Feature-Flag <code>ingredientsDb</code>).</p></div>`;
    return;
  }
  el.innerHTML = `<div class="card"><p class="hint">Lade Food-Cost…</p></div>`;
  const cache = window.App && window.App.nutritionCache;
  if (cache && cache.ensureLoaded) { try { await cache.ensureLoaded(); } catch (_) {} }
  el.innerHTML = renderReportHtml();
}

/** Rückwärtskompatibel (Alt-Tab #tab-food-cost). */
export async function renderFoodCostReport() { return renderFoodCostInto('tab-food-cost'); }

// Eine Zeile je verkaufbarem Gericht (option/extra sind Komponenten → raus).
function buildRows() {
  const cache = window.App && window.App.nutritionCache;
  const target = globalTarget();
  const dishes = menuState().filter(m => m && !m.deletedAt && m.itemType !== 'option' && m.itemType !== 'extra');
  return dishes.map(m => {
    const cost = (cache && cache.composeCostSync) ? cache.composeCostSync(m.id, {}) : null;
    const hasRecipe = !!(cost && cost.ingredientCount > 0);
    const goods = hasRecipe ? cost.goodsCost : null;
    const price = (m.price != null && isFinite(Number(m.price))) ? Number(m.price) : null;
    const pct = hasRecipe ? foodCostPct(goods, price) : null;
    const margin = hasRecipe ? marginEuro(price, goods) : null;
    const tgt = targetPctFor(m.meta, target);
    // S158-Hotfix: missing_data (Einkauf unvollständig) → grau, nicht grün.
    const band = hasRecipe ? ampelFor({ pct, missingData: !!(cost && cost.missingData), targetPct: tgt }) : 'unknown';
    return {
      id: m.id, name: m.name || '—', price, goods, pct, margin, band, tgt,
      missing: !!(cost && cost.missingData), hasRecipe
    };
  });
}

function sortRows(rows) {
  const dir = _sortDir === 'asc' ? 1 : -1;
  const val = r => {
    switch (_sortKey) {
      case 'name': return (r.name || '').toLowerCase();
      case 'price': return r.price == null ? -Infinity : r.price;
      case 'goods': return r.goods == null ? -Infinity : r.goods;
      case 'margin': return r.margin == null ? -Infinity : r.margin;
      case 'pct':
      default: return r.pct == null ? -Infinity : r.pct; // ohne Daten ans Ende
    }
  };
  return rows.slice().sort((a, b) => {
    const va = val(a), vb = val(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return (a.name || '').localeCompare(b.name || '');
  });
}

function th(key, label, align) {
  const active = _sortKey === key;
  const arrow = active ? (_sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return `<th style="text-align:${align || 'left'}; cursor:pointer; user-select:none;" onclick="fcSort('${key}')" title="Sortieren">${label}${arrow}</th>`;
}

function renderReportHtml() {
  const rows = sortRows(buildRows());
  const target = globalTarget();
  const dot = band => `<span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:${AMPEL_COLOR[band]};" title="${AMPEL_LABEL[band]}"></span>`;

  const body = rows.map(r => {
    const grey = (!r.hasRecipe || r.band === 'unknown') ? 'color:var(--ink-muted);' : '';
    const note = !r.hasRecipe ? '<span class="hint" style="font-size:.74rem;"> · kein Rezept</span>'
      : (r.missing ? '<span class="hint" style="font-size:.74rem; color:var(--danger);"> · Einkauf unvollständig</span>' : '');
    return `
      <tr style="border-top:1px solid var(--border); ${grey}">
        <td style="padding:6px 8px;">${htmlEscape(r.name)}${note}</td>
        <td style="padding:6px 8px; text-align:right;">${formatEuro(r.price)}</td>
        <td style="padding:6px 8px; text-align:right;">${formatEuro(r.goods)}</td>
        <td style="padding:6px 8px; text-align:right;">${formatPct(r.pct)}</td>
        <td style="padding:6px 8px; text-align:right;">${formatEuro(r.margin)}</td>
        <td style="padding:6px 8px; text-align:center;" title="${AMPEL_LABEL[r.band]} (Ziel ≤ ${r.tgt} %)">${dot(r.band)}</td>
      </tr>`;
  }).join('');

  return `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;">
        <div>
          <h2 style="margin:0;">Food-Cost / Wareneinsatz</h2>
          <p class="hint" style="margin:4px 0 0;">Wareneinsatz je Gericht (Standard-Komposition inkl. Default-Optionen). ${rows.length} Gericht(e). Ampel: grün ≤ Ziel · gelb ≤ Ziel+5 · rot darüber.</p>
        </div>
        <label style="display:flex; align-items:center; gap:6px; font-size:.85rem;">
          Ziel-Food-Cost
          <input type="number" min="1" max="100" step="1" value="${target}" style="width:64px;" onchange="fcSetTarget(this.value)"> %
        </label>
      </div>
      <div class="stats-ranklist${isRankOpen('ww-foodcost') ? ' is-open' : ''}" data-rankkey="ww-foodcost" data-total="${rows.length}">
        <div style="margin-top:14px; overflow-x:auto;">
          <table style="width:100%; border-collapse:collapse; font-size:.9rem;">
            <thead><tr style="text-align:left; color:var(--ink-soft);">
              ${th('name', 'Gericht', 'left')}
              ${th('price', 'Preis', 'right')}
              ${th('goods', 'Wareneinsatz', 'right')}
              ${th('pct', 'Food-Cost %', 'right')}
              ${th('margin', 'Marge', 'right')}
              <th style="text-align:center;">Ampel</th>
            </tr></thead>
            <tbody class="stats-rank-body">${body || '<tr><td colspan="6" style="padding:16px; text-align:center;" class="hint">Noch keine verkaufbaren Gerichte mit Rezept.</td></tr>'}</tbody>
          </table>
        </div>
        ${rows.length > 5 ? `<button type="button" class="stats-rank-toggle" onclick="statsToggleRank(this)">${isRankOpen('ww-foodcost') ? 'weniger anzeigen' : `alle anzeigen (${rows.length})`}</button>` : ''}
      </div>
    </div>`;
}

export function fcSort(key) {
  if (_sortKey === key) _sortDir = (_sortDir === 'asc') ? 'desc' : 'asc';
  else { _sortKey = key; _sortDir = (key === 'name') ? 'asc' : 'desc'; }
  const el = _host();
  if (el) el.innerHTML = renderReportHtml();
}

export async function fcSetTarget(val) {
  const n = Number(val);
  if (!isFinite(n) || n <= 0 || n > 100) { showToast('Ziel-% muss 1–100 sein', 'error'); return; }
  try {
    await storage().settings.update({ foodCostTargetPct: n });
    const el = _host();
    if (el) el.innerHTML = renderReportHtml();
  } catch (e) {
    console.error('[food-cost] target save:', e);
    showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

if (typeof window !== 'undefined') {
  Object.assign(window, { renderFoodCostReport, renderFoodCostInto, fcSort, fcSetTarget });
}
